import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SPECIALIST_LANES, PROJECTS, EVIDENCE_STATUSES } from "../config/wiseboardConfig";
import { toast } from "sonner";
import { Users, AlertTriangle, CheckCircle, ShieldAlert, Save } from "lucide-react";

interface RosterEntry {
  id?: string;
  lane: string;
  selected: boolean;
  reason: string | null;
}

interface EvidenceRow {
  hypothesis_id: string;
  status: string;
  updated_by_project: string | null;
}

interface Props {
  caseId: string;
  onRosterChange?: () => void;
}

export default function SpecialistRosterManager({ caseId, onRosterChange }: Props) {
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;
    const load = async () => {
      const [rosterRes, evidenceRes] = await Promise.all([
        supabase.from("wiseboard_specialist_roster").select("id, lane, selected, reason").eq("case_id", caseId),
        supabase.from("wiseboard_evidence_matrix").select("hypothesis_id, status, updated_by_project").eq("case_id", caseId),
      ]);

      // Build full roster from SPECIALIST_LANES, merging DB rows
      const dbMap: Record<string, RosterEntry> = {};
      (rosterRes.data || []).forEach((r: any) => { dbMap[r.lane] = r; });

      const full = SPECIALIST_LANES.map(lane => ({
        lane,
        id: dbMap[lane]?.id,
        selected: dbMap[lane]?.selected ?? false,
        reason: dbMap[lane]?.reason ?? null,
      }));
      setRoster(full);
      setEvidence(evidenceRes.data || []);
      setLoading(false);
    };
    load();
  }, [caseId]);

  const toggleLane = (lane: string) => {
    setRoster(prev => prev.map(r => r.lane === lane ? { ...r, selected: !r.selected } : r));
  };

  const updateReason = (lane: string, reason: string) => {
    setRoster(prev => prev.map(r => r.lane === lane ? { ...r, reason } : r));
  };

  const saveRoster = async () => {
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Not authenticated"); setSaving(false); return; }

    for (const entry of roster) {
      if (entry.id) {
        await supabase.from("wiseboard_specialist_roster")
          .update({ selected: entry.selected, reason: entry.reason })
          .eq("id", entry.id);
      } else {
        await supabase.from("wiseboard_specialist_roster").insert({
          case_id: caseId,
          user_id: user.id,
          lane: entry.lane,
          selected: entry.selected,
          reason: entry.reason,
        });
      }
    }

    // Reload to get IDs
    const { data } = await supabase.from("wiseboard_specialist_roster")
      .select("id, lane, selected, reason").eq("case_id", caseId);
    if (data) {
      const dbMap: Record<string, RosterEntry> = {};
      data.forEach((r: any) => { dbMap[r.lane] = r; });
      setRoster(SPECIALIST_LANES.map(lane => ({
        lane,
        id: dbMap[lane]?.id,
        selected: dbMap[lane]?.selected ?? false,
        reason: dbMap[lane]?.reason ?? null,
      })));
    }

    toast.success("Specialist roster saved");
    onRosterChange?.();
    setSaving(false);
  };

  // Gate-check: fact sufficiency per lane
  const getFactSufficiency = (lane: string) => {
    const laneEvidence = evidence.filter(e => e.updated_by_project === lane);
    if (laneEvidence.length === 0) return { total: 0, found: 0, missing: 0, stale: 0, status: "no_data" as const };
    const found = laneEvidence.filter(e => e.status === "DATA_FOUND").length;
    const missing = laneEvidence.filter(e => e.status === "NOT_FOUND").length;
    const stale = laneEvidence.filter(e => e.status === "POTENTIALLY_STALE").length;
    const ratio = found / laneEvidence.length;
    return {
      total: laneEvidence.length,
      found,
      missing,
      stale,
      status: ratio >= 0.7 ? "sufficient" as const : ratio >= 0.4 ? "partial" as const : "insufficient" as const,
    };
  };

  const selectedCount = roster.filter(r => r.selected).length;
  const excludedCount = roster.length - selectedCount;

  if (loading) return <div className="text-sm text-muted-foreground">Loading roster...</div>;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-amber-500" />
            <CardTitle className="text-base">Specialist Roster Manager</CardTitle>
          </div>
          <Button size="sm" onClick={saveRoster} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving..." : "Save Roster"}
          </Button>
        </div>
        <CardDescription className="text-xs">
          Toggle P3–P11 lanes on/off. Provide a reason for each selection. {selectedCount} selected · {excludedCount} excluded
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {roster.map(entry => {
          const project = PROJECTS[entry.lane];
          if (!project) return null;
          const sufficiency = getFactSufficiency(entry.lane);

          return (
            <div
              key={entry.lane}
              className={`rounded-lg border p-3 transition-all ${entry.selected ? "border-amber-400 bg-amber-50/50 dark:bg-amber-950/20" : "border-muted opacity-60"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Switch
                    checked={entry.selected}
                    onCheckedChange={() => toggleLane(entry.lane)}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{entry.lane}</span>
                      <span className="text-sm text-muted-foreground truncate">{project.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{project.legacyName} · {project.role}</p>
                  </div>
                </div>

                {/* Fact sufficiency badge */}
                <div className="shrink-0">
                  {sufficiency.status === "no_data" ? (
                    <Badge variant="outline" className="text-[10px] gap-1">
                      <AlertTriangle className="h-3 w-3" /> No evidence
                    </Badge>
                  ) : sufficiency.status === "sufficient" ? (
                    <Badge className="bg-green-100 text-green-800 text-[10px] gap-1">
                      <CheckCircle className="h-3 w-3" /> {sufficiency.found}/{sufficiency.total} found
                    </Badge>
                  ) : sufficiency.status === "partial" ? (
                    <Badge className="bg-yellow-100 text-yellow-800 text-[10px] gap-1">
                      <AlertTriangle className="h-3 w-3" /> {sufficiency.found}/{sufficiency.total} found
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800 text-[10px] gap-1">
                      <ShieldAlert className="h-3 w-3" /> {sufficiency.found}/{sufficiency.total} found
                    </Badge>
                  )}
                </div>
              </div>

              {entry.selected && (
                <div className="mt-2">
                  <Textarea
                    value={entry.reason || ""}
                    onChange={e => updateReason(entry.lane, e.target.value)}
                    placeholder="Reason for selecting this lane (e.g. 'Critical for competitive positioning assessment')..."
                    rows={2}
                    className="text-xs"
                  />
                </div>
              )}

              {/* Sufficiency detail when there's data */}
              {sufficiency.total > 0 && entry.selected && (
                <div className="mt-2 flex gap-3 text-[10px] text-muted-foreground">
                  {sufficiency.missing > 0 && <span className="text-red-600">{sufficiency.missing} missing</span>}
                  {sufficiency.stale > 0 && <span className="text-yellow-600">{sufficiency.stale} stale</span>}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
